import type { MarkerWarning } from "../board/parse-board-markers";

/**
 * Visible, honest correction shown under an assistant message when one
 * or more of Nikola's board markers were rejected (see
 * parse-board-markers.ts). Deliberately visible rather than silent —
 * for players learning the rules of chess, seeing "this specific claim
 * couldn't be verified" is more useful than a seamless-looking answer
 * that happens to have a wrong detail baked in.
 *
 * Shows the actual rejected marker (e.g. "d1-h1") rather than a generic
 * "something was wrong" message — a vague warning is worse than none
 * when a reply has several arrows and only one was bad, since the
 * player can't tell which part to distrust.
 */
export const CorrectionNotice = ({ warnings }: { warnings: MarkerWarning[] }) => {
  if (warnings.length === 0) return null;

  return (
    <div className="correction-notice" role="note">
      {warnings.map((w, i) => (
        <p key={i} className="correction-notice__item">
          ⚠️ {describeWarning(w)}
        </p>
      ))}
    </div>
  );
};

const describeWarning = (warning: MarkerWarning): string => {
  switch (warning.kind) {
    case "illegal-arrow":
      return `The move "${warning.raw}" isn't legal in this position, so that arrow wasn't drawn.`;
    case "invalid-fen":
      return `A board position couldn't be verified, so it wasn't shown.`;
    case "invalid-square":
      return `"${warning.raw}" isn't a real square, so that highlight wasn't shown.`;
    case "orphan-marker":
      return `An annotation ("${warning.raw}") was given before any position, so it was skipped.`;
    case "invalid-orientation":
      return `"${warning.raw}" isn't a valid board side, so the orientation didn't change.`;
  }
};
